export type AiMappingErrorCode =
  | "unauthorized"
  | "task_not_found"
  | "unavailable"
  | "request_failed"
  | "area_not_found"
  | "detection_not_found"
  | "segmentation_not_found"
  | "unknown_category"
  | "invalid_proposal_product"
  | "invalid_additional_cost"
  | "run_not_found"
  | "validation"
  | "unknown";

export interface AiMappingErrorInfo {
  code: AiMappingErrorCode;
  message: string;
  detail: string | null;
}

export const AI_MAPPING_UNAUTHORIZED_MESSAGE = "This action is unauthorized.";

const FRIENDLY_MESSAGES: Record<AiMappingErrorCode, string> = {
  unauthorized: "AI mapping requires the AI mapping add-on subscription.",
  task_not_found: "The detection task could not be found. Please run detection again.",
  unavailable: "The AI detection service is temporarily unavailable. Please try again.",
  request_failed: "The AI detection request failed. Please try again.",
  area_not_found: "This measurement no longer exists on the AI service. Refreshing the list…",
  detection_not_found: "This detection no longer exists. Refreshing the results…",
  segmentation_not_found: "This area no longer exists. Refreshing the results…",
  unknown_category: "This detection's category is no longer recognized. Refreshing the results…",
  invalid_proposal_product:
    "The selected proposal product is no longer valid. Please pick another.",
  invalid_additional_cost: "The selected additional cost is no longer valid. Please pick another.",
  run_not_found: "The AI mapping run no longer exists. Refreshing…",
  validation: "Some AI mapping input is invalid.",
  unknown: "Something went wrong with AI mapping. Please try again.",
};

interface GraphQLErrorLike {
  message?: string;
  extensions?: {
    category?: string;
    validation?: Record<string, string[]>;
  };
}

interface ApolloErrorLike {
  graphQLErrors?: GraphQLErrorLike[];
  message?: string;
}

const flattenValidationErrors = (validation?: Record<string, string[]>): string | null => {
  if (!validation) return null;
  const messages = Object.entries(validation).flatMap(([field, errors]) =>
    errors.map((error) => error.replace("input.", "").replace(field, field.replace("input.", "")))
  );
  return messages.length ? messages.join(" ") : null;
};

const matchCode = (message: string): AiMappingErrorCode | null => {
  if (message === AI_MAPPING_UNAUTHORIZED_MESSAGE || message.includes("unauthorized")) {
    return "unauthorized";
  }
  if (message.includes("ai_mapping_task_not_found")) return "task_not_found";
  if (message.includes("ai_mapping_unavailable")) return "unavailable";
  if (message.includes("ai_mapping_request_failed")) return "request_failed";
  if (message.includes("ai_mapping_area_not_found")) return "area_not_found";
  if (message.includes("ai_mapping_detection_not_found")) return "detection_not_found";
  if (message.includes("ai_mapping_segmentation_not_found")) return "segmentation_not_found";
  if (message.includes("ai_mapping_unknown_category")) return "unknown_category";
  if (message.includes("ai_mapping_run_not_found")) return "run_not_found";
  if (message.includes("invalid_proposal_product")) return "invalid_proposal_product";
  if (message.includes("invalid_additional_cost")) return "invalid_additional_cost";
  return null;
};

const isStaleTaskError = (
  rawMessage: string,
  validationMessage: string | null,
  validation?: Record<string, string[]>
): boolean => {
  if (rawMessage.includes("task id is invalid")) return true;
  if (validationMessage?.includes("taskId")) return true;
  return !!validation && Object.keys(validation).some((key) => key.endsWith("taskId"));
};

export function classifyAiMappingError(error: unknown): AiMappingErrorInfo {
  const apolloError = (error ?? {}) as ApolloErrorLike;
  const gqlError = apolloError.graphQLErrors?.[0];
  const rawMessage = gqlError?.message ?? apolloError.message ?? "";

  const validationMessage = flattenValidationErrors(gqlError?.extensions?.validation);

  if (isStaleTaskError(rawMessage, validationMessage, gqlError?.extensions?.validation)) {
    return {
      code: "task_not_found",
      message: FRIENDLY_MESSAGES.task_not_found,
      detail: rawMessage,
    };
  }

  if (gqlError?.extensions?.category === "validation" || validationMessage) {
    return {
      code: "validation",
      message: FRIENDLY_MESSAGES.validation,
      detail: validationMessage,
    };
  }

  const code = matchCode(rawMessage) ?? "unknown";
  return {
    code,
    message: FRIENDLY_MESSAGES[code],
    detail: code === "unknown" ? rawMessage || null : rawMessage !== code ? rawMessage : null,
  };
}

export function isAiMappingUnauthorizedError(error: unknown): boolean {
  return classifyAiMappingError(error).code === "unauthorized";
}
